import {Injectable} from '@angular/core';
import Typesense, {Client} from 'typesense';
import {from, Observable, of} from 'rxjs';
import {IStore} from '../../shared-models/store/store';
import {ISearchableFields} from '../../shared-models/type-sense/default-searchable-fields';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import {
  convertFilterObjectToTypesenseFilter,
  convertSearchableFields,
  mapTypeSenseObjectToGeneric,
} from '../../shared-utils/typesense-utils/typesense.utils';
import {
  TypeSenseFacetCounts,
  TypeSenseHit,
  TypeSenseSearchResponse,
} from '../../shared-models/type-sense/type-sense-types';
import {
  activeRefinementFacetFields,
  mergeDisjunctiveFacetCounts,
  searchStockItemsCreateFilterString,
} from './type-sense.service.utils';
import {ITypeSenseQuery} from '../../shared-models/type-sense/type-sense.queries';
import {
  IAdvancedFilterGroup,
} from '../../../features/settings-new/models';
import {Store} from '@ngrx/store';
import {map, mergeMap, take} from 'rxjs/operators';
import {selectSelectedStoreTypeSenseSearchApiKey} from '../../../features/stock-manager/store/stock-manager.selectors';
import {environment} from '../../../../environments/environment';
import {SearchParams} from 'typesense/lib/Typesense/Documents';

/** Earliest `_stock_take_date` treated as present in Typesense (seconds). Excludes null/missing and 0; includes placeholders such as 1990-01-01. */
const STOCK_TAKE_DATE_PRESENT_FLOOR_SEC = 1;

/**
 * The Typesense client only joins array params (e.g. `query_by`, `infix`) into comma
 * strings for single-document `search()` (see `SearchOnlyDocuments.search`). `multiSearch.perform`
 * posts the given params as-is, and the API rejects raw JSON arrays there with a 400. Join them here
 * so multi-search requests behave the same as a single search.
 */
function toMultiSearchParams<T extends Record<string, unknown>>(params: T): T {
  const joined = {...params};
  Object.keys(joined).forEach((key: string) => {
    const value = (joined as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      (joined as Record<string, unknown>)[key] = value.join(',');
    }
  });
  return joined;
}

function appendAdvancedFilterToFilterString(
  filterString: string,
  advancedFilters?: IAdvancedFilterGroup,
  options?: { omitStringPresence?: boolean },
): string {
  if (!advancedFilters) {
    return filterString;
  }

  const advancedFilterString = convertFilterObjectToTypesenseFilter(advancedFilters, options);
  if (!advancedFilterString) {
    return filterString;
  }

  if (filterString.length > 0) {
    return `${filterString} && ${advancedFilterString}`;
  }
  return advancedFilterString;
}

@Injectable({
  providedIn: 'root',
})
/**
 * @class TypeSenseService
 * A service class for interacting with the TypeSense API.
 * Initializes the TypeSense client and provides methods to perform searches.
 */
export class TypeSenseService {

  /**
   * The TypeSense client instance used to interact with the TypeSense API.
   *
   * @member {Client} client
   */
  private client: Client;

  /**
   * Constructs the TypeSenseService and initializes the TypeSense client.
   *
   * @constructor
   */
  constructor(
    private readonly store: Store,
  ) {
  }

  /**
   * Searches stock items with pagination.
   *
   * @param {string} query - The search query string.
   * @param {number} page - The page number for pagination.
   * @param {number} perPage - The number of items per page.
   * @param {IStore} store - The currently selected store.
   * @param {ISearchableFields} query_by - The codes of the fields that you want the search to query.
   * @param {Object<string, ISearchableFields>} [filters] - The list of suppliers that you want to add to filters; value must be true to add to filters.
   * @param sort
   * @param advancedFilters
   * @returns {Observable<TypeSenseSearchResponse<StockItem>>} An observable emitting the search results.
   */
  searchStockItems(
    query: string,
    page: number,
    perPage: number,
    store: IStore,
    query_by: ISearchableFields,
    filters?: Record<string, ISearchableFields>,
    sort?: string,
    advancedFilters?: IAdvancedFilterGroup,
    advancedFilterOptions?: { omitStringPresence?: boolean },
  ): Observable<TypeSenseSearchResponse<StockItem>> {
    const searchParamsResult = this.buildStockItemsSearchParams(
      query,
      page,
      perPage,
      query_by,
      filters,
      sort,
      advancedFilters,
      advancedFilterOptions,
      '3,4,40,46,_tags,multipleSuppliers',
    );
    if (searchParamsResult instanceof Observable) {
      return searchParamsResult;
    }

    this.initializeClient();
    if (this.client) {
      return this.executeStockSearch(
        this.client,
        store.storeId,
        searchParamsResult,
        filters,
        advancedFilters,
        advancedFilterOptions,
        true,
      );
    }
    return from([] as unknown as Observable<TypeSenseSearchResponse<StockItem>>);
  }

  /**
   * Searches a store's Typesense collection with an explicit search API key.
   * Does not replace the shared Typesense client used by single-store stock manager.
   */
  searchStockItemsWithApiKey(
    query: string,
    page: number,
    perPage: number,
    store: IStore,
    searchApiKey: string,
    query_by: ISearchableFields,
    filters?: Record<string, ISearchableFields>,
    sort?: string,
    advancedFilters?: IAdvancedFilterGroup,
    advancedFilterOptions?: { omitStringPresence?: boolean },
    facetBy: string = '3,4,40,_tags,multipleSuppliers',
    disjunctiveFacets?: boolean,
  ): Observable<TypeSenseSearchResponse<StockItem>> {
    if (!searchApiKey) {
      return of({hits: [], facet_counts: [], found: 0, out_of: 0, page, request_params: null} as TypeSenseSearchResponse<StockItem>);
    }

    const searchParamsResult = this.buildStockItemsSearchParams(
      query,
      page,
      perPage,
      query_by,
      filters,
      sort,
      advancedFilters,
      advancedFilterOptions,
      facetBy,
    );
    if (searchParamsResult instanceof Observable) {
      return searchParamsResult;
    }

    const client = new Typesense.Client({
      nodes: [{host: environment.typeSenseKey + '-1.a1.typesense.net', port: 443, protocol: 'https'}],
      apiKey: searchApiKey,
      connectionTimeoutSeconds: 10,
    });

    return this.executeStockSearch(
      client,
      store.storeId,
      searchParamsResult,
      filters,
      advancedFilters,
      advancedFilterOptions,
      disjunctiveFacets ?? page === 1,
    );
  }

  private buildStockItemsSearchParams(
    query: string,
    page: number,
    perPage: number,
    query_by: ISearchableFields,
    filters?: Record<string, ISearchableFields>,
    sort?: string,
    advancedFilters?: IAdvancedFilterGroup,
    advancedFilterOptions?: { omitStringPresence?: boolean },
    facetBy: string = '3,4,40,46,_tags,multipleSuppliers',
  ): SearchParams | Observable<TypeSenseSearchResponse<StockItem>> {
    const fieldArray = convertSearchableFields(query_by);
    let filterString = '';
    try {
      filterString = this.refinementFilterString(filters, advancedFilters, advancedFilterOptions);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      return from(Promise.reject(new Error(`Advanced filter could not be applied: ${message}`)));
    }

    if (!sort) {
      sort = '1:asc';
    }
    type Infix = 'off' | 'always' | 'fallback'
    const infixArray: Infix[] = [];
    if (typeof fieldArray !== 'string') {
      fieldArray.forEach((field: string) => {
        if (field === '1') {
          infixArray.push(`always`);
        } else {
          infixArray.push(`off`);
        }
      });
    }

    return {
      q: query,
      query_by: fieldArray,
      page: page,
      filter_by: filterString,
      facet_by: facetBy,
      max_facet_values: 500,
      infix: infixArray,
      sort_by: query === '' ? sort : `_text_match:desc,${sort}`,
      per_page: perPage,
    };
  }

  /**
   * Runs the stock search. When a refinement facet has a selection, companion searches
   * rebuild that facet without its own filter so further values that still match the
   * other refinements stay available.
   */
  private executeStockSearch(
    client: Client,
    storeId: string,
    searchParams: SearchParams,
    filters: Record<string, ISearchableFields> | undefined,
    advancedFilters: IAdvancedFilterGroup | undefined,
    advancedFilterOptions: { omitStringPresence?: boolean } | undefined,
    disjunctiveFacets: boolean,
  ): Observable<TypeSenseSearchResponse<StockItem>> {
    const facetFields = disjunctiveFacets ? activeRefinementFacetFields(filters) : [];
    let searches: (SearchParams & { collection: string })[];
    try {
      searches = [
        toMultiSearchParams({collection: storeId, ...searchParams}),
        ...facetFields.map((field: string) => toMultiSearchParams({
          collection: storeId,
          ...searchParams,
          filter_by: this.refinementFilterString(filters, advancedFilters, advancedFilterOptions, field),
          facet_by: field,
          page: 1,
          per_page: 1,
        })),
      ];
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      return from(Promise.reject(new Error(`Advanced filter could not be applied: ${message}`)));
    }

    return from(client.multiSearch.perform({searches})).pipe(
      map((multiResp: {
        results: (TypeSenseSearchResponse<StockItem> & { error?: string })[];
      }) => {
        const main = multiResp.results[0];
        if (!main || main.error) {
          throw new Error(main?.error || 'Stock search failed');
        }
        return mergeDisjunctiveFacetCounts(main, multiResp.results.slice(1), facetFields);
      }),
    );
  }

  private refinementFilterString(
    filters: Record<string, ISearchableFields> | undefined,
    advancedFilters?: IAdvancedFilterGroup,
    advancedFilterOptions?: { omitStringPresence?: boolean },
    omitField?: string,
  ): string {
    let filterString = '';
    if (filters && Object.keys(filters).length > 0) {
      filterString = searchStockItemsCreateFilterString(filters, omitField);
    }
    return appendAdvancedFilterToFilterString(filterString, advancedFilters, advancedFilterOptions);
  }

  multiSearchEditedStockItems(
    query: string,
    page: number,
    store: IStore,
    query_by: Record<string, {name: string; value: boolean}>,
    editedItems: Record<string, StockItem>,
    maxKeysPerChunk: number = 200,
  ): Observable<TypeSenseSearchResponse<StockItem>> {
    this.initializeClient();
    if (!this.client) {
      return of({hits: [], facet_counts: [], found: 0} as TypeSenseSearchResponse<StockItem>);
    }

    const fieldArrayOrString = convertSearchableFields(query_by);
    const fieldString =
      Array.isArray(fieldArrayOrString) && fieldArrayOrString.length > 0
        ? fieldArrayOrString.join(',')
        : '';

    const allEditedKeys = Object.keys(editedItems || {});
    if (!fieldString || allEditedKeys.length === 0) {
      return from(
        this.client
          .collections(store.storeId)
          .documents()
          .search({
            q: query,
            query_by: fieldString,
            page,
            per_page: 250,
            facet_by: '3,4,40,46,_tags,multipleSuppliers',
            max_facet_values: 500,
            sort_by: '1:asc',
          } as ITypeSenseQuery),
      ) as Observable<TypeSenseSearchResponse<StockItem>>;

    }
    const chunks: string[][] = [];
    for (let i = 0; i < allEditedKeys.length; i += maxKeysPerChunk) {
      chunks.push(allEditedKeys.slice(i, i + maxKeysPerChunk));
    }

    const multiSearchObjects: ITypeSenseQuery[] = chunks.map((chunkedKeys: string[]) => {
      const quotedKeys = chunkedKeys.map((key: string) => `"${key.replace(/"/g, '\\"')}"`);
      const filterValue = `0:=[${quotedKeys.join(',')}]`;

      return {
        collection: store.storeId,
        q: query,
        query_by: fieldString,
        page,
        per_page: 250,
        filter_by: filterValue,
        facet_by: '3,4,40,46,_tags,multipleSuppliers',
        max_facet_values: 500,
        sort_by: '1:asc',
      } as ITypeSenseQuery;
    });

    return from(
      this.client.multiSearch.perform({
        searches: multiSearchObjects,
      }),
    ).pipe(
      mergeMap((multiResp: {results: TypeSenseSearchResponse<StockItem>[]}) => {
        const allHits: StockItem[] = [];
        let finalFacetCounts: TypeSenseFacetCounts[] = [];

        multiResp.results.forEach((subResp: TypeSenseSearchResponse<StockItem>) => {
          const mappedHits = mapTypeSenseObjectToGeneric<StockItem>(subResp);
          allHits.push(...mappedHits);

          if (!finalFacetCounts.length && Array.isArray(subResp.facet_counts)) {
            finalFacetCounts = subResp.facet_counts;
          }
        });
        const combinedResponse: TypeSenseSearchResponse<StockItem> = {
          hits: allHits.map((doc: StockItem) => ({document: doc} as unknown as TypeSenseHit<StockItem>)),
          facet_counts: finalFacetCounts,
          found: multiResp.results[0]?.found ?? allHits.length,
          out_of: 0,
          page: 0,
          request_params: undefined,
        };

        return of(combinedResponse);
      }),
    );
  }


  searchFilteredStockItems(
    query: string,
    page: number,
    store: IStore,
    query_by: ISearchableFields,
    advancedFilters?: IAdvancedFilterGroup,
    filters?: Record<string, ISearchableFields>,
  ): Observable<TypeSenseSearchResponse<StockItem>> {
    let filterString = '';
    if (filters && Object.keys(filters).length > 0) {
      filterString = searchStockItemsCreateFilterString(filters);
    }

    try {
      filterString = appendAdvancedFilterToFilterString(filterString, advancedFilters);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      return from(Promise.reject(new Error(`Advanced filter could not be applied: ${message}`)));
    }

    const fieldArray = convertSearchableFields(query_by);
    const searchParams: ITypeSenseQuery = {
      q: query,
      query_by: fieldArray,
      page: page,
      filter_by: filterString,
      facet_by: '3,4,40,46,_tags,multipleSuppliers',
      max_facet_values: 500,
      sort_by: '1:asc',
      per_page: 250,
    };
    this.initializeClient();
    if (this.client) {
      const typeSenseData: Observable<TypeSenseSearchResponse<StockItem>> = from(this.client
        .collections(store.storeId)
        .documents()
        .search(searchParams) as unknown as Observable<TypeSenseSearchResponse<StockItem>>);
      if (typeSenseData) {
        return typeSenseData;
      } else {
        from([] as unknown as Observable<TypeSenseSearchResponse<StockItem>>);
      }
    } else return from([] as unknown as Observable<TypeSenseSearchResponse<StockItem>>);
  }

  /**
   * Returns the number of stock items matching a Typesense filter.
   * When the filter references a field missing from the schema, `schemaReady` is false.
   */
  countStockItemsByFilter(
    store: IStore,
    filterBy: string,
  ): Observable<{ found: number; schemaReady: boolean }> {
    this.initializeClient();
    if (!this.client) {
      return of({ found: 0, schemaReady: false });
    }
    return from(this.runCountSearch(store, filterBy));
  }

  /**
   * Counts stock items with a null/missing/zero `_stock_take_date` in Typesense and reports schema readiness.
   * Placeholder dates such as 1990-01-01 count as present (indexed); never-counted logic uses a separate cutoff.
   */
  checkStockTakeDateIndexHealth(
    store: IStore,
    dateField: string,
  ): Observable<{ found: number; schemaReady: boolean }> {
    this.initializeClient();
    if (!this.client) {
      return of({ found: 0, schemaReady: false });
    }
    return from(this.probeStockTakeDateIndexHealth(store, dateField));
  }

  /**
   * Returns whether the Typesense index exposes the stock take date field for filtering.
   */
  async hasCollectionField(store: IStore, dateField: string): Promise<boolean> {
    this.initializeClient();
    if (!this.client) {
      return false;
    }
    const result = await this.probeStockTakeDateIndexHealth(store, dateField);
    return result.schemaReady;
  }

  private async probeStockTakeDateIndexHealth(
    store: IStore,
    dateField: string,
  ): Promise<{ found: number; schemaReady: boolean }> {
    if (!this.client) {
      return { found: 0, schemaReady: false };
    }

    const schemaReady = await this.isStockTakeDateFieldIndexed(store, dateField);
    if (!schemaReady) {
      return { found: 0, schemaReady: false };
    }

    const totalCount = await this.runDocumentCountSearch(store);
    if (totalCount == null) {
      return { found: 0, schemaReady: false };
    }

    const presentCount = await this.runDocumentCountSearch(
      store,
      `${dateField}:>=${STOCK_TAKE_DATE_PRESENT_FLOOR_SEC}`,
    );
    if (presentCount == null) {
      return { found: 0, schemaReady: false };
    }

    return {
      found: Math.max(0, totalCount - presentCount),
      schemaReady: true,
    };
  }

  /**
   * True when the collection schema exposes the stock take date field for sort/filter.
   */
  private async isStockTakeDateFieldIndexed(store: IStore, dateField: string): Promise<boolean> {
    if (!this.client) {
      return false;
    }
    try {
      await this.client.collections(store.storeId).documents().search({
        q: '*',
        query_by: '0',
        sort_by: `${dateField}:asc`,
        page: 1,
        per_page: 1,
      });
      return true;
    } catch {
      return false;
    }
  }

  private async runDocumentCountSearch(store: IStore, filterBy?: string): Promise<number | null> {
    if (!this.client) {
      return null;
    }
    try {
      const searchParams: SearchParams = {
        q: '*',
        query_by: '0',
        page: 1,
        per_page: 1,
      };
      if (filterBy) {
        searchParams.filter_by = filterBy;
      }
      const response = await this.client.collections(store.storeId).documents().search(searchParams);
      return response?.found ?? 0;
    } catch {
      return null;
    }
  }

  private async runCountSearch(
    store: IStore,
    filterBy: string,
  ): Promise<{ found: number; schemaReady: boolean }> {
    if (!this.client) {
      return { found: 0, schemaReady: false };
    }
    try {
      const response = await this.client.collections(store.storeId).documents().search({
        q: '*',
        query_by: '0',
        filter_by: filterBy,
        page: 1,
        per_page: 1,
      });
      return { found: response?.found ?? 0, schemaReady: true };
    } catch (error: unknown) {
      if (this.isTypesenseStockTakeDateFilterError(error)) {
        return { found: 0, schemaReady: false };
      }
      return { found: 0, schemaReady: false };
    }
  }

  private isTypesenseSchemaFieldMissingError(error: unknown): boolean {
    const httpStatus = (error as { httpStatus?: number })?.httpStatus;
    if (httpStatus === 404) {
      return true;
    }
    const message = error instanceof Error ? error.message : String(error ?? '');
    return message.includes('Could not find a field') || message.includes('field named');
  }

  private isTypesenseStockTakeDateFilterError(error: unknown): boolean {
    if (this.isTypesenseSchemaFieldMissingError(error)) {
      return true;
    }
    const httpStatus = (error as { httpStatus?: number })?.httpStatus;
    if (httpStatus === 400) {
      return true;
    }
    const message = error instanceof Error ? error.message : String(error ?? '');
    return message.includes('Not found') || message.includes('filter');
  }

  private initializeClient(): void {
    let typesenseApiKey = '';
    this.store.select(selectSelectedStoreTypeSenseSearchApiKey).pipe(take(1)).subscribe((apiKey: string) => {
      typesenseApiKey = apiKey;
      if (typesenseApiKey) {
        this.client = new Typesense.Client({
          nodes: [{host: environment.typeSenseKey + '-1.a1.typesense.net', port: 443, protocol: 'https'}],
          apiKey: typesenseApiKey,
          connectionTimeoutSeconds: 10,
        });
      }
    });
  }

}

